import { useEffect, useRef } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';
import { reportFatal, serializeError } from '@/lib/errorReport';
import { ErrorFallback } from './ErrorFallback';

/**
 * `errorElement` da rota-raiz do `createBrowserRouter` do admin: reporta o erro
 * de rota (`admin_route_crash`, FATAL) e mostra o mesmo fallback i18n do
 * `ErrorBoundary` global, sem stack.
 *
 * Origem: #795 — 2026-10-03 14:38 +0200 (auditoria geral, OBS-3).
 *
 * Por quê: com um *data router* (`createBrowserRouter`) o React Router 7
 * captura todo erro de render das rotas no `RenderErrorBoundary` interno dele.
 * Sem `errorElement` em nenhuma rota, cai no `DefaultErrorComponent`: só
 * `console.error`, e o operador vê "Unexpected Application Error!" com a
 * mensagem e o stack. O `ErrorBoundary` do `main.tsx` (#12 §3.4.3) fica POR
 * FORA do `RouterProvider` e nunca recebe esses erros — então o
 * `admin_render_crash` nunca era emitido para crash de tela.
 *
 * Contrato: lê o erro com `useRouteError()`; efeito colateral único é
 * `reportFatal('admin_route_crash', …)` uma vez por erro (o ref segura o duplo
 * efeito do `StrictMode` em dev). `ErrorResponse` (status de loader — hoje não
 * há loaders) vai como `status`/`statusText`; qualquer outra coisa passa por
 * `serializeError` (`errorName`/`errorMessage`/`errorStack` ≤ 1,5 kB).
 *
 * Usado por: `routes/router.tsx` (rota-raiz pathless que envolve TODAS as
 * rotas, inclusive `/login`). Só web `apps/admin`; irmãos: `app_route_crash`
 * (`apps/app`, #795 WEB-2) e `crm_route_crash` (`apps/crm`, #795 OBS-3).
 *
 * Arquitetura: o `ErrorBoundary` do `main.tsx` continua cobrindo o que mora
 * fora das rotas (`AppVersionGate`, `InstallPrompt`, `WebPushBootstrap`). O
 * boundary do router reseta sozinho ao mudar de `location`, então navegar para
 * outra rota recupera a tela sem recarregar.
 *
 * Dados — evento PostHog `admin_route_crash` (via `reportFatal`):
 *   { "severity": "FATAL", "errorName": "TypeError",
 *     "errorMessage": "x is undefined", "errorStack": "…(≤1500)" }
 * ou, para `ErrorResponse`: { "severity": "FATAL", "status": 404, "statusText": "Not Found" }.
 * A URL vem do próprio PostHog (`$current_url`, redigida pelo `before_send`).
 *
 * Dicas: não trocar por `ErrorBoundary` em volta do `RouterProvider` — é
 * exatamente o que não funciona com data router. Testes:
 * `RouteErrorBoundary.test.tsx` e `routes/router.test.tsx`.
 *
 * Issues: #795 (OBS-3), #12 (§3.4.3, ErrorBoundary global).
 *
 * Histórico:
 *  - 2026-10-03 14:38 +0200 — (#795, OBS-3) criado.
 */
export function RouteErrorBoundary(): React.JSX.Element {
  const error = useRouteError();
  const reportedRef = useRef<unknown>(undefined);

  useEffect(() => {
    if (reportedRef.current === error) return;
    reportedRef.current = error;
    reportFatal(
      'admin_route_crash',
      isRouteErrorResponse(error)
        ? { status: error.status, statusText: error.statusText }
        : serializeError(error),
    );
  }, [error]);

  return <ErrorFallback />;
}
